import { useQueryClient } from '@tanstack/react-query'
import { Outlet, useNavigate } from '@tanstack/react-router'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useState, type FormEvent } from 'react'
import { AuthLink, AuthShell, Field, Notice, SubmitButton } from '@/components/auth/AuthCard'
import { Loading } from '@/components/shell/Loading'
import { Icon } from '@/components/ui/Icon'
import { ThemeMenu } from '@/components/workspace/ThemeMenu'
import { rememberWorkspace, useApi, useMe } from '@/lib/api'
import { authClient, sanitizeNext } from '@/lib/auth-client'
import { apiFetch, errorMessage } from '@/lib/client-api'
import { ROLE_LABELS } from '@/lib/roles'
import { btn, cx } from '@/lib/ui'

/** The bar above sign-in and friends: sign-out for someone already in, and the theme. */
export function PublicLayout() {
  const me = useMe()
  const queryClient = useQueryClient()
  async function signOut() {
    await authClient.signOut()
    await queryClient.invalidateQueries()
  }
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex h-16 shrink-0 items-center justify-end gap-3 px-5 sm:px-8">
        {me.data && (
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-muted sm:block">{me.data.email}</span>
            <button type="button" onClick={signOut} className={cx(btn.sm, btn.secondary)}>
              <Icon name="logout" className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        )}
        <ThemeMenu variant="segments" />
      </header>
      <main className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  )
}

function useAfterAuth() {
  const params = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const next = sanitizeNext(params.get('next'))
  return {
    next,
    done: async () => {
      await queryClient.invalidateQueries()
      await navigate({ to: next as never })
    },
  }
}

function form(onSubmit: (data: FormData) => Promise<void>, setError: (m: string | null) => void, setBusy: (b: boolean) => void) {
  return async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await onSubmit(new FormData(event.currentTarget))
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }
}

const authError = (result: { error?: { message?: string } | null }, fallback: string) => {
  if (result.error) throw new Error(result.error.message || fallback)
}

export function LoginPage() {
  const { next, done } = useAfterAuth()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const me = useMe()
  if (me.data) {
    void done()
    return <Loading />
  }
  return (
    <AuthShell
      title="Sign in to ROUTR"
      intro="Plan your signal flow, from a two-mic podcast to a four-camera rig."
      footer={
        <>
          New here? <AuthLink href={`/register${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}>Create an account</AuthLink>
        </>
      }
    >
      <form
        onSubmit={form(
          async (data) => {
            const result = await authClient.signIn.email({ email: String(data.get('email')), password: String(data.get('password')) })
            authError(result, 'Wrong email or password.')
            await done()
          },
          setError,
          setBusy,
        )}
      >
        <Field id="email" name="email" type="email" labelText="Email" autoComplete="email" autoFocus />
        <Field id="password" name="password" type="password" labelText="Password" autoComplete="current-password" />
        {error && <Notice tone="error">{error}</Notice>}
        <SubmitButton>{busy ? 'Signing in…' : 'Sign in'}</SubmitButton>
      </form>
      <p className="mt-4 text-center text-xs text-muted">
        <AuthLink href="/forgot-password">Forgot your password?</AuthLink>
      </p>
    </AuthShell>
  )
}

export function RegisterPage() {
  const { done } = useAfterAuth()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  return (
    <AuthShell
      title="Create your account"
      intro="Then name a workspace for your studio, your show or your company."
      footer={
        <>
          Already have one? <AuthLink href="/login">Sign in</AuthLink>
        </>
      }
    >
      <form
        onSubmit={form(
          async (data) => {
            const name = String(data.get('name') ?? '').trim()
            const email = String(data.get('email'))
            const result = await authClient.signUp.email({ name: name || email.split('@')[0]!, email, password: String(data.get('password')) })
            authError(result, 'Could not create the account.')
            await done()
          },
          setError,
          setBusy,
        )}
      >
        <Field id="name" name="name" labelText="Your name" autoComplete="name" placeholder="Rina Putri" required={false} autoFocus />
        <Field id="email" name="email" type="email" labelText="Email" autoComplete="email" />
        <Field id="password" name="password" type="password" labelText="Password" autoComplete="new-password" minLength={8} placeholder="At least 8 characters" />
        {error && <Notice tone="error">{error}</Notice>}
        <SubmitButton>{busy ? 'Creating…' : 'Create account'}</SubmitButton>
      </form>
    </AuthShell>
  )
}

export function ForgotPasswordPage() {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  return (
    <AuthShell title="Reset your password" intro="We will email you a link to choose a new one." footer={<AuthLink href="/login">Back to sign in</AuthLink>}>
      <form
        onSubmit={form(
          async (data) => {
            const result = await authClient.requestPasswordReset({ email: String(data.get('email')), redirectTo: '/reset-password' })
            authError(result, 'Could not send the link.')
            setSent(true)
          },
          setError,
          setBusy,
        )}
      >
        <Field id="email" name="email" type="email" labelText="Email" autoComplete="email" autoFocus />
        {error && <Notice tone="error">{error}</Notice>}
        {sent && <Notice tone="success">If that address has an account, a reset link is on its way.</Notice>}
        <SubmitButton>{busy ? 'Sending…' : 'Send reset link'}</SubmitButton>
      </form>
    </AuthShell>
  )
}

export function ResetPasswordPage() {
  const params = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  return (
    <AuthShell title="Choose a new password" footer={<AuthLink href="/login">Back to sign in</AuthLink>}>
      {!token ? (
        <Notice tone="error">That link is missing its token. Request a new one.</Notice>
      ) : (
        <form
          onSubmit={form(
            async (data) => {
              const result = await authClient.resetPassword({ newPassword: String(data.get('password')), token })
              authError(result, 'That link has expired. Request a new one.')
              await navigate({ to: '/login' })
            },
            setError,
            setBusy,
          )}
        >
          <Field id="password" name="password" type="password" labelText="New password" autoComplete="new-password" minLength={8} placeholder="At least 8 characters" autoFocus />
          {error && <Notice tone="error">{error}</Notice>}
          <SubmitButton>{busy ? 'Saving…' : 'Save password'}</SubmitButton>
        </form>
      )}
    </AuthShell>
  )
}

type InvitePeek = { workspaceName: string; role: 'ADMIN' | 'MEMBER' }

export function InvitePage({ token }: { token: string }) {
  const me = useMe()
  const invite = useApi<InvitePeek>(`/api/invites/${token}`)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (invite.isPending || me.isPending) return <Loading />
  if (invite.isError) {
    return (
      <AuthShell title="This invite link is no longer valid">
        <Notice tone="error">{errorMessage(invite.error, 'Ask the person who invited you for a fresh link.')}</Notice>
      </AuthShell>
    )
  }
  const next = `/invite/${token}`
  async function join() {
    setBusy(true)
    setError(null)
    try {
      const result = await apiFetch<{ workspace: { slug: string } }>(`/api/invites/${token}/accept`, { method: 'POST' })
      rememberWorkspace(result.workspace.slug)
      await queryClient.invalidateQueries()
      await navigate({ to: `/w/${result.workspace.slug}/projects` as never })
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }
  return (
    <AuthShell
      title={`Join ${invite.data.workspaceName}`}
      intro={`You have been invited as ${ROLE_LABELS[invite.data.role].toLowerCase()}. Members share the workspace's projects, equipment and inventory.`}
    >
      {error && <Notice tone="error">{error}</Notice>}
      {me.data ? (
        <div>
          <p className="mt-5 text-sm text-muted">
            Signed in as <span className="font-medium text-foreground">{me.data.email}</span>.
          </p>
          <button type="button" onClick={join} disabled={busy} className={cx(btn.base, btn.primary, 'mt-5 w-full')}>
            {busy ? 'Joining…' : 'Join workspace'}
          </button>
        </div>
      ) : (
        <div className="mt-5 grid gap-2">
          <Link href={`/register?next=${encodeURIComponent(next)}`} className={cx(btn.base, btn.primary, 'w-full')}>
            Create an account to join
          </Link>
          <Link href={`/login?next=${encodeURIComponent(next)}`} className={cx(btn.base, btn.secondary, 'w-full')}>
            I already have an account
          </Link>
        </div>
      )}
    </AuthShell>
  )
}

export function OnboardingPage() {
  const me = useMe()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (me.isPending) return <Loading />
  if (!me.data) {
    void navigate({ to: '/login', search: { next: '/onboarding' } as never })
    return <Loading />
  }
  const workspaces = me.data.workspaces
  return (
    <AuthShell
      title={workspaces.length === 0 ? 'Create your workspace' : 'Create another workspace'}
      intro="A workspace holds your projects, your equipment and your inventory. Work alone in it, or invite people later."
    >
      <form
        onSubmit={form(
          async (data) => {
            const ws = await apiFetch<{ slug: string }>('/api/workspaces', { method: 'POST', body: JSON.stringify({ name: String(data.get('name')) }) })
            rememberWorkspace(ws.slug)
            await queryClient.invalidateQueries()
            await navigate({ to: `/w/${ws.slug}/projects` as never })
          },
          setError,
          setBusy,
        )}
      >
        <Field id="name" name="name" labelText="Workspace name" placeholder="Mfalme Studio" minLength={2} autoFocus />
        <p className="-mt-2 mb-4 text-xs text-muted">Your studio, your show or your company. You can invite others later.</p>
        {error && <Notice tone="error">{error}</Notice>}
        <SubmitButton>{busy ? 'Creating…' : 'Create workspace'}</SubmitButton>
      </form>
      {workspaces.length > 0 && (
        <div className="mt-7 border-t border-[var(--border)] pt-5">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">Your workspaces</p>
          <ul className="mt-2 space-y-1">
            {workspaces.map((workspace) => (
              <li key={workspace.id}>
                <Link href={`/w/${workspace.slug}/projects`} className="flex items-center justify-between rounded-xl px-2.5 py-2 text-sm transition-colors hover:bg-hover">
                  <span>{workspace.name}</span>
                  <span className="rounded-full bg-[var(--surface-raised)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted">{ROLE_LABELS[workspace.role]}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AuthShell>
  )
}

/** The front door: a visitor gets the landing page; someone signed in goes to their last workspace. */
export function HomePage({ Landing }: { Landing: React.ComponentType<{ signupsOpen: boolean }> }) {
  const me = useMe()
  const navigate = useNavigate()
  if (me.isPending) return <Loading />
  if (!me.data) return <Landing signupsOpen />
  const workspaces = me.data.workspaces
  if (workspaces.length === 0) {
    void navigate({ to: '/onboarding' })
    return <Loading />
  }
  const remembered = window.localStorage.getItem('routr_ws')
  const target = workspaces.find((w) => w.slug === remembered) ?? workspaces[0]!
  void navigate({ to: `/w/${target.slug}/projects` as never, replace: true })
  return <Loading />
}
